/** Minimal rect for the matched token (viewport coordinates). */
export type AnchorWordRect = {
  top: number;
  left: number;
  bottom: number;
  right: number;
};

export type ClampSynonymPopoverOptions = {
  /** Upper bound for popover width (matches SynonymSuggestions max-w). */
  maxPopoverWidth?: number;
  /** Upper bound for popover height (list max-h + heading + padding). */
  maxPopoverHeight?: number;
  margin?: number;
  gapAbove?: number;
};

/** Approximate SynonymSuggestions outer height for viewport math (must stay in sync with UI). */
export const estimateSynonymPopoverHeight = (matchCount: number): number => {
  const rows = Math.max(1, matchCount);
  const chromePx = 52;
  const rowPx = 40;
  const listCapPx = 200;
  const bodyPx = Math.min(rows * rowPx, listCapPx);
  return Math.min(280, chromePx + bodyPx + 12);
};

/**
 * Pin a fixed-position synonym suggestion popover inside the viewport.
 * Prefers the caller's placement below the anchor; flips above when there is
 * not enough room below.
 */
export const clampSynonymPopoverToViewport = (
  preferredTopLeft: { top: number; left: number },
  anchorWordRect: AnchorWordRect,
  options?: ClampSynonymPopoverOptions
): { top: number; left: number } => {
  if (typeof window === "undefined") {
    return preferredTopLeft;
  }

  const margin = options?.margin ?? 8;
  const gapAbove = options?.gapAbove ?? 4;
  /** Default when omitted: conservative but smaller than old 280 so flip-above stays near the caret. */
  const maxH = options?.maxPopoverHeight ?? 200;
  const maxWCap = options?.maxPopoverWidth ?? 300;

  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const effectiveMaxW = Math.min(maxWCap, vw - 2 * margin);

  let left = preferredTopLeft.left;
  let top = preferredTopLeft.top;

  left = Math.max(margin, Math.min(left, vw - effectiveMaxW - margin));

  const fitsBelow = top + maxH <= vh - margin;
  if (!fitsBelow) {
    const aboveTop = anchorWordRect.top - maxH - gapAbove;
    if (aboveTop >= margin) {
      top = aboveTop;
    } else {
      top = Math.max(margin, vh - maxH - margin);
    }
  }

  if (top + maxH > vh - margin) {
    top = Math.max(margin, vh - maxH - margin);
  }
  if (top < margin) {
    top = margin;
  }

  return { top, left };
};
